import type {Writable} from 'svelte/store';
import {writable, get} from 'svelte/store';

import {errors} from 'Store/investorProfile/errorsStore';
import type {InvestorProfileErrors} from 'Store/investorProfile/errorsStore';
import {selectedProfileId} from 'Store/investorProfile/selectedProfileIdStore';
import {loading} from 'Store/investorProfile/loadingStore';
import internalApi from 'Utility/internalApi';
import type {
  EntitySubTypes,
  EntityTypes,
} from 'Store/investorProfile/profilesStore';
import {
  buildCorporationEntityBody,
  buildIndividualBody,
  buildJointBody,
  buildTrustEntityBody,
  buildManagedEntityBody,
  mapCorporationProfile,
  mapIndividualProfile,
  mapJointProfile,
  mapTrustProfile,
  mapManagedProfile,
} from 'Utility/investorCheckout/profileTypes';
import type {EntityResponse} from 'Utility/investorCheckout/profileTypes';
import {currentUserId} from 'Store/currentUserIdStore';
import type {CaAccreditedOptionValue} from 'Types/investorProfile/accreditedOptionTypes';

export type EntityType = `${EntityTypes}` | '';
export type EntitySubType = `${EntitySubTypes}` | '';

export type BaseEntity = {
  firstName: string;
  lastName?: string;
  title?: string;
  name?: string;
  suffix?: string;
  dateOfBirth?: string;
  date?: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  country: string;
  countryCode: string;
  region: string;
  regionCode: string;
  tin: string;
  income?: number;
  netWorth?: number;
  regCFPriorOfferingsAmount?: number;
  phoneNumber?: string;
  accountNumber?: string;
  /** Current user's email */
  email?: string;
  /** Email of any third party */
  emailAlternate?: string;
  /** One of option from enum EntityTypes */
  type?: EntityType;
  /** Human readable form of `subTypeCode` */
  subType?: string;
  /** One of option from enum EntitySubTypes when the entity `type` is joint, blank string/unset otherwise */
  subTypeCode?: EntitySubType;
  usAccreditedCategory?: string;
  caAccreditedCategory?: CaAccreditedOptionValue;
  complete?: boolean;
  /** Whenever the user explicit consent to any agreement before proceeding further */
  confirmation?: boolean;
};

export type Entity = {
  signingOfficer?: Writable<BaseEntity>;
  jointHolder?: Writable<BaseEntity>;
  trustees?: Writable<Writable<BaseEntity>[]>;
  beneficialOwners?: Writable<Writable<BaseEntity>[]>;
  beneficiary?: Writable<BaseEntity>;
} & BaseEntity;

export const baseEntity: BaseEntity = {
  firstName: '',
  lastName: '',
  title: '',
  suffix: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postalCode: '',
  country: '',
  countryCode: '',
  region: '',
  regionCode: '',
  dateOfBirth: '',
  date: '',
  tin: '',
  phoneNumber: '',
  email: '',
  emailAlternate: '',
  accountNumber: '',
  income: null,
  netWorth: null,
  regCFPriorOfferingsAmount: null,
  usAccreditedCategory: '',
  caAccreditedCategory: null,
  complete: false,
  confirmation: false,
};

export const defaultEntity = (): Entity => ({
  ...baseEntity,
  type: '',
  subTypeCode: '',
  subType: '',
  signingOfficer: writable<BaseEntity>(Object.assign({}, baseEntity)),
  jointHolder: writable<BaseEntity>(Object.assign({}, baseEntity)),
  beneficiary: writable<BaseEntity>(Object.assign({}, baseEntity)),
  trustees: writable([writable<BaseEntity>(Object.assign({}, baseEntity))]),
  beneficialOwners: writable([
    writable<BaseEntity>(Object.assign({}, baseEntity)),
  ]),
});

const raiseInvalidTypeError = () => {
  throw new Error('Profile type is not supported');
};

const PROFILE_MAP = {
  individual: mapIndividualProfile,
  corporation: mapCorporationProfile,
  joint: mapJointProfile,
  trust: mapTrustProfile,
  managed: mapManagedProfile,
  '': raiseInvalidTypeError,
};

export const buildPayload = (entity: Entity) => {
  const payload = PROFILE_MAP[entity?.type ?? ''](entity);
  return {
    ...payload,
    acting_user_id: get(currentUserId),
  };
};

const PROFILE_BODY = {
  individual: buildIndividualBody,
  joint: buildJointBody,
  corporation: buildCorporationEntityBody,
  trust: buildTrustEntityBody,
  managed: buildManagedEntityBody,
  '': raiseInvalidTypeError,
};

export const buildBody = (entity: EntityResponse) => {
  return PROFILE_BODY[entity?.type ?? ''](entity);
};

const entityStore = () => {
  const entity = writable<Entity>(defaultEntity());
  const {set, subscribe, update} = entity;

  return {
    set,
    subscribe,
    update,
    createProfile: async (): Promise<void> => {
      const profile: Entity = get(entity);
      const payload: string = JSON.stringify(buildPayload(profile));
      const response: Response = await internalApi.post(
        `investor_profiles/${profile.type}s`,
        {
          body: payload,
          headers: {'Content-Type': 'application/json'},
        }
      );

      if (response?.ok) {
        loading.set(true);
        const body = await response.json();
        await selectedProfileId.updateProfile(body.id);
        entity.set(defaultEntity());
        errors.set({});
      } else {
        const json = await response.json();
        const e: InvestorProfileErrors = json['error'];
        errors.set(e);
      }
    },
  };
};

export const entity = entityStore();
